import { FCWithChildren } from '@types';
import React, { useReducer } from 'react';

import { Tooltip } from 'react-tooltip';
import { FaPen, FaTrash } from 'react-icons/fa';
import {
  MdComputer,
  MdDesktopMac,
  MdDevicesOther,
  MdLaptopMac,
  MdPhoneIphone,
  MdPrint,
  MdRouter,
  MdSportsEsports,
  MdTabletMac,
  MdTv,
  MdWatch,
} from 'react-icons/md';

import { Button, StatusInfo, StatusLed } from '@components';

import EditDeviceModal from '../EditDeviceModal';
import RemoveDeviceModal from '../RemoveDeviceModal';
import useLayoutContext from '@hooks/useLayoutContext';
import { MomentInput } from 'moment';
import { IconType } from 'react-icons';

type DeviceProps = {
  id: number;
  lastLogin?: MomentInput | undefined;
  macAddress: string;
  name: string;
  deviceType: string;
  status: string;
  openStatusInfoModal: (str: string) => void;
};

const Device: FCWithChildren<DeviceProps> = ({
  id,
  lastLogin,
  macAddress,
  name,
  deviceType,
  status,
  openStatusInfoModal,
}) => {
  const [isOpenEditDeviceModal, toggleEditDeviceModal] = useReducer(
    (isOpenEditDeviceModal) => !isOpenEditDeviceModal,
    false,
  );

  const [isOpenRemoveDeviceModal, toggleRemoveDeviceModal] = useReducer(
    (isOpenRemoveDeviceModal) => !isOpenRemoveDeviceModal,
    false,
  );

  const { canManageDevices } = useLayoutContext();

  const getDeviceIcon = (): IconType => {
    const value = `${deviceType} ${name}`.toLowerCase();

    if (
      value.includes('iphone') ||
      value.includes('android') ||
      value.includes('phone') ||
      value.includes('pixel')
    ) {
      return MdPhoneIphone;
    }

    if (value.includes('ipad') || value.includes('tablet')) {
      return MdTabletMac;
    }

    if (value.includes('watch')) {
      return MdWatch;
    }

    if (value.includes('playstation') || value.includes('xbox') || value.includes('nintendo')) {
      return MdSportsEsports;
    }

    if (
      value.includes('tv') ||
      value.includes('roku') ||
      value.includes('fire') ||
      value.includes('appletv')
    ) {
      return MdTv;
    }

    if (value.includes('router')) {
      return MdRouter;
    }

    if (value.includes('printer')) {
      return MdPrint;
    }

    if (value.includes('laptop') || value.includes('macbook')) {
      return MdLaptopMac;
    }

    if (value.includes('desktop') || value.includes('imac')) {
      return MdDesktopMac;
    }

    if (value.includes('computer') || value.includes('pc')) {
      return MdComputer;
    }

    return MdDevicesOther;
  };

  const DeviceIcon = getDeviceIcon();

  return (
    <div className="border-b border-gray-300 last:border-b-0">
      <RemoveDeviceModal
        id={id}
        closeModal={toggleRemoveDeviceModal}
        isOpen={isOpenRemoveDeviceModal}
      />
      <EditDeviceModal
        id={id}
        name={name}
        isOpen={isOpenEditDeviceModal}
        closeModal={toggleEditDeviceModal}
      />
      <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-start gap-x-3 px-3 py-3 sm:flex sm:items-center sm:justify-between sm:gap-4 sm:px-0 sm:py-5">
        <div className="col-start-1 row-span-2 flex h-full shrink-0 flex-col items-center justify-between text-gray-800 sm:h-11 sm:w-11 sm:justify-center">
          <DeviceIcon size={34} />
          <div className="mb-3 sm:hidden">
            <StatusLed dataTip dataFor={`device-${id}`} type={status} />
          </div>
        </div>

        <div className="col-start-2 min-w-0 sm:flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="truncate text-base font-medium leading-none text-black sm:text-xl">
              {name}
            </span>
            <span className="text-sm text-gray-500">{deviceType}</span>
          </div>
          <div className="mt-1 truncate text-sm text-gray-500">{macAddress}</div>
        </div>

        <div className="col-start-2 row-start-2 mt-2 flex items-start text-sm text-gray-500 sm:mt-0 sm:items-center sm:gap-2">
          <div className="hidden sm:block">
            <StatusLed dataTip dataFor={`device-${id}`} type={status} />
          </div>
          <StatusInfo
            lastLogin={lastLogin}
            status={status}
            id={id}
            openStatusInfoModal={openStatusInfoModal}
          />
        </div>

        {canManageDevices && (
          <div className="col-start-3 row-span-2 flex items-center gap-2 self-center">
            <Tooltip id={`device-edit-${id}`} place="top">
              Rename
            </Tooltip>
            <Button
              layout="link"
              size="noPadding"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-gray-100 text-blue-700"
              data-tip
              data-for={`device-edit-${id}`}
              onClick={toggleEditDeviceModal}
              aria-label="Rename"
            >
              <FaPen />
            </Button>
            <Tooltip id={`device-remove-${id}`} place="top">
              Remove
            </Tooltip>
            <Button
              layout="link"
              size="noPadding"
              className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-gray-100 text-blue-700"
              data-tip
              data-for={`device-remove-${id}`}
              onClick={toggleRemoveDeviceModal}
              aria-label="Remove"
            >
              <FaTrash />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Device;
